import React, { useEffect, useMemo, useState } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";

const FALLBACK_PAGES = [
  {
    slug: "cash-balance-plan-business-owners",
    title: "Cash Balance Plans for Business Owners",
    h1: "Cash Balance Plans for Business Owners",
    excerpt:
      "A cash balance plan can help profitable business owners compress years of retirement funding into a disciplined, tax-aware strategy.",
    url: "/cash-balance-plan-business-owners/",
    published_at: "",
  },
];

function formatDate(value) {
  if (!value) return "Published resource";
  const parsed = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return "Published resource";
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function normalizePage(page) {
  const slug = String(page.slug || "").replace(/^\/+|\/+$/g, "");
  const url = page.url || (slug ? `/${slug}/` : "/publications/");
  return {
    ...page,
    slug,
    url,
    title: page.title || page.h1 || "Lion Pride Financial Publication",
    excerpt:
      page.excerpt ||
      page.meta_description ||
      "Educational planning content from Lion Pride Financial.",
  };
}

export default function Publications() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadPages() {
      setLoading(true);
      setError("");

      try {
        const res = await fetch("/api/publications.php", {
          method: "GET",
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        });

        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          throw new Error(
            `Publications API returned non-JSON: ${text.slice(0, 120)}`
          );
        }

        if (!res.ok || data.success === false) {
          throw new Error(data.error || `Publications API failed with HTTP ${res.status}`);
        }

        if (!cancelled) {
          const nextPages = Array.isArray(data.pages) ? data.pages.map(normalizePage) : [];
          setPages(nextPages.length ? nextPages : FALLBACK_PAGES.map(normalizePage));
        }
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          setError(err.message || "Unable to load publications right now.");
          setPages(FALLBACK_PAGES.map(normalizePage));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPages();
    return () => {
      cancelled = true;
    };
  }, []);

  const publishedPages = useMemo(
    () => pages.filter((page) => page.slug || page.url),
    [pages]
  );

  return (
    <>
      <Navbar />

      <main className="min-h-screen bg-gradient-to-b from-slate-950 via-blue-950 to-slate-900 text-white">
        <section className="max-w-6xl mx-auto px-6 pt-20 pb-12">
          <div className="max-w-3xl">
            <p className="uppercase tracking-[0.22em] text-cyan-300 text-sm font-semibold mb-4">
              Lion Pride Financial Publications
            </p>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
              Advanced planning insights for business owners and families.
            </h1>
            <p className="text-lg md:text-xl text-slate-200 leading-relaxed">
              Explore educational articles on cash balance plans, business exit planning,
              liquidity strategies, retirement income, and protection planning.
            </p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 pb-20">
          {loading && (
            <div className="rounded-2xl border border-cyan-400/30 bg-white/10 p-6 text-slate-100">
              Loading publications…
            </div>
          )}

          {!loading && error && (
            <div className="mb-6 rounded-2xl border border-yellow-400/40 bg-yellow-500/10 p-5 text-yellow-100">
              Publications API warning: {error}. Showing available fallback content.
            </div>
          )}

          {!loading && publishedPages.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/10 p-8">
              <h2 className="text-2xl font-semibold mb-3">No publications are live yet.</h2>
              <p className="text-slate-200">
                Publish a scheduled SEO page from the admin dashboard and it will appear here.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {publishedPages.map((page) => (
              <article
                key={page.slug || page.url}
                className="rounded-3xl border border-white/10 bg-white/10 backdrop-blur p-7 shadow-xl hover:bg-white/[0.14] transition"
              >
                <p className="text-sm text-cyan-200 mb-3">
                  {formatDate(page.published_at || page.updated_at)}
                </p>
                <h2 className="text-2xl font-bold mb-4 leading-snug">
                  <a href={page.url} className="hover:text-cyan-300 transition">
                    {page.title}
                  </a>
                </h2>
                <p className="text-slate-200 leading-relaxed mb-6">{page.excerpt}</p>
                <a
                  href={page.url}
                  className="inline-flex items-center rounded-xl bg-cyan-500 hover:bg-cyan-400 text-white px-5 py-3 font-semibold transition"
                >
                  Read publication
                  <span aria-hidden="true" className="ml-2">→</span>
                </a>
              </article>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
